/**
 * CentroScommessePage — `/scommesse` (Andrea 2026-06-13).
 *
 * Centro scommesse per i presidenti:
 *   - P2P: se la mia squadra è una delle 2 della partita, posso proporre/
 *     accettare una sfida contro l'altro presidente (importo a testa, chi
 *     vince prende tutto, pareggio → rimborso).
 *   - Banco: se NON sono coinvolto e il banco è attivo, punto su 1/X/2 con
 *     le quote fissate dall'admin (vincita = puntata × quota).
 *
 * Le scommesse chiudono all'inizio della giornata (countdown = deadline
 * formazione). Crediti = budget squadra (fantamilioni).
 *
 * Andrea 2026-09-17: sotto le partite aperte c'e' il RESOCONTO pubblico —
 * chi ha scommesso cosa, giornata per giornata, con vincite possibili, esiti
 * e bilanci (ResocontoScommesse). Ha preso il posto dell'elenco "chiuse /
 * risolte", che mostrava solo le proprie.
 */

import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useStagioneAttiva } from "../../hooks/useStagione";
import { useMieSquadre, useSquadre } from "../../hooks/useFase7";
import { MobileShell } from "../shell/MobileShell";
import {
  useCartello,
  useResocontoScommesse,
  useScommesseCartello,
  useProponiP2P,
  useRispondiP2P,
  usePiazzaBanco,
  type CartelloItem,
  type EsitoPartita,
  type ScommessaP2PItem,
  type ScommessaBancoItem,
} from "../../hooks/useScommesse";
import { etichetta } from "../common/etichette";
import { ResocontoScommesse } from "./ResocontoScommesse";
import { tr } from "../../i18n";

function errMsg(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err.response?.data?.detail ?? err.message ?? tr("Errore");
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return "00:00:00";
  const t = Math.floor(ms / 1000);
  const d = Math.floor(t / 86400);
  const h = Math.floor((t % 86400) / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const p = (n: number) => String(n).padStart(2, "0");
  return d > 0 ? `${d}g ${p(h)}:${p(m)}:${p(s)}` : `${p(h)}:${p(m)}:${p(s)}`;
}

const ESITO_LABEL: Record<EsitoPartita, string> = {
  "1": tr("1 (casa)"),
  x: tr("X (pareggio)"),
  "2": tr("2 (trasferta)"),
};

export function CentroScommessePage() {
  const { user } = useAuth();
  const { data: stagione } = useStagioneAttiva();
  const stagioneId = stagione?.id ?? null;
  const { data: mieSquadre } = useMieSquadre(user?.id);
  const mieIds = (mieSquadre ?? []).map((s) => s.id);

  const { data: cartelli, isLoading } = useCartello({ stagioneId });
  const lista = cartelli ?? [];
  const aperti = lista.filter((c) => c.stato === "aperta" && !c.chiusa);
  const { data: resoconto } = useResocontoScommesse(stagioneId);

  return (
    <MobileShell logoSuffix="SQUAD BET">
      <div className="bg-gradient-to-br from-bg-card to-primary-deep border border-accent/30 rounded-card p-4 flex items-center gap-3">
        <div className="text-3xl">🎰</div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base text-accent tracking-wider">SQUAD BET</div>
          <div className="text-xs text-fg-muted">
            {tr(
              tr(
                "Sfida i presidenti (P2P) o punta col banco · si chiude all'inizio della giornata · sotto, chi ha scommesso cosa",
              ),
            )}
          </div>
        </div>
      </div>

      {!stagioneId && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center text-fg-muted">
          {tr("Nessuna stagione attiva.")}
        </div>
      )}

      {stagioneId && isLoading && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center text-fg-muted">
          {tr("Caricamento…")}
        </div>
      )}

      {stagioneId && !isLoading && lista.length === 0 && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center">
          <div className="text-4xl mb-2">🎲</div>
          <div className="text-fg-muted text-sm">
            {tr(
              tr(
                "Nessuna partita di cartello al momento. L'admin deve selezionarle dalla gestione.",
              ),
            )}
          </div>
        </div>
      )}

      {aperti.length > 0 && (
        <div>
          <h3 className="font-display text-lg tracking-wider text-fg mb-2">{tr("PUNTA ADESSO")}</h3>
          <div className="space-y-2">
            {aperti.map((c) => (
              <CartelloCard key={c.id} cartello={c} mieIds={mieIds} />
            ))}
          </div>
        </div>
      )}

      {/* Andrea 2026-09-17: resoconto pubblico, giornata per giornata */}
      {resoconto && resoconto.giornate.length > 0 && <ResocontoScommesse resoconto={resoconto} />}
    </MobileShell>
  );
}

// ============================================================
// Countdown alla chiusura
// ============================================================
function Countdown({ deadline }: { deadline: string | null }) {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!deadline) return <span className="text-fg-muted">{tr("orario non disponibile")}</span>;
  const remaining = new Date(deadline).getTime() - now;
  if (remaining <= 0) return <span className="text-danger">{tr("chiuse")}</span>;
  const urgente = remaining < 60 * 60 * 1000;
  return (
    <span className={`tabular-nums ${urgente ? "text-warning" : "text-accent"}`}>
      chiude tra {formatRemaining(remaining)}
    </span>
  );
}

const ESITO_BADGE: Record<EsitoPartita, string> = {
  "1": tr("Vince casa (1)"),
  x: tr("Pareggio (X)"),
  "2": tr("Vince trasferta (2)"),
};

// ============================================================
// Card di un cartello (P2P + banco) per l'utente
// ============================================================
function CartelloCard({ cartello, mieIds }: { cartello: CartelloItem; mieIds: string[] }) {
  const c = cartello;
  const { data: scommesse } = useScommesseCartello(c.id);
  const { data: squadre } = useSquadre();
  const nomeSquadra = (id: string): string =>
    squadre?.find((s) => s.id === id)?.nome ?? id.slice(0, 8);

  // La mia squadra coinvolta nella partita (per P2P), se ce n'è una.
  const miaCoinvolta =
    mieIds.find((id) => id === c.squadra_casa_id || id === c.squadra_trasferta_id) ?? null;
  // Le mie squadre NON coinvolte (per il banco).
  const mieEsterne = mieIds.filter(
    (id) => id !== c.squadra_casa_id && id !== c.squadra_trasferta_id,
  );

  const bettabile = c.stato === "aperta" && !c.chiusa;

  return (
    <div className="bg-bg-card border border-primary/20 rounded-card p-3">
      {/* Header partita. Christian 2026-10-30: nomi a tutta larghezza (su
          mobile il countdown accanto li tagliava), sotto competizione e
          countdown sulla stessa riga. */}
      <div className="font-display text-sm text-fg">
        {c.squadra_casa_nome} <span className="text-fg-muted">vs</span> {c.squadra_trasferta_nome}
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        {/* Andrea 2026-10-30: con campionato e coppa nella stessa giornata
            serve sapere di quale competizione e' la partita. */}
        <div className="text-[10px] text-fg-muted font-mono truncate min-w-0">
          {c.competizione_nome
            ? `${c.competizione_tipo === "campionato" ? "📊" : "🏆"} ${c.competizione_nome}`
            : ""}
        </div>
        {c.stato === "risolta" ? (
          <span className="shrink-0 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-pill border bg-info/15 text-info border-info/40">
            {c.esito ? ESITO_BADGE[c.esito] : "RISOLTA"}
          </span>
        ) : (
          <span className="shrink-0 text-[10px] font-mono">
            <Countdown deadline={c.deadline} />
          </span>
        )}
      </div>

      {/* P2P — solo se la mia squadra è coinvolta */}
      {miaCoinvolta && (
        <BloccoP2P
          cartello={c}
          miaSquadraId={miaCoinvolta}
          p2p={scommesse?.p2p ?? []}
          bettabile={bettabile}
          nomeSquadra={nomeSquadra}
        />
      )}

      {/* Banco — solo se attivo e ho squadre non coinvolte */}
      {c.banco_attivo && mieEsterne.length > 0 && (
        <BloccoBanco
          cartello={c}
          mieEsterne={mieEsterne}
          bancoMie={(scommesse?.banco ?? []).filter((b) => mieEsterne.includes(b.squadra_id))}
          bettabile={bettabile}
          nomeSquadra={nomeSquadra}
        />
      )}

      {/* Se non sono coinvolto e il banco non è attivo */}
      {!miaCoinvolta && !c.banco_attivo && (
        <div className="text-[11px] text-fg-muted mt-2">
          {tr("Solo sfida tra i 2 presidenti (banco non attivo).")}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Blocco P2P
// ============================================================
function BloccoP2P({
  cartello,
  miaSquadraId,
  p2p,
  bettabile,
  nomeSquadra,
}: {
  cartello: CartelloItem;
  miaSquadraId: string;
  p2p: ScommessaP2PItem[];
  bettabile: boolean;
  nomeSquadra: (id: string) => string;
}) {
  const proponi = useProponiP2P();
  const rispondi = useRispondiP2P();
  const [importo, setImporto] = useState("");
  const [errore, setErrore] = useState<string | null>(null);

  const viva = p2p.find((s) => ["proposta", "accettata"].includes(s.stato));
  const risolta = p2p.find((s) => s.stato === "risolta");

  async function fProponi() {
    setErrore(null);
    try {
      await proponi.mutateAsync({
        cartelloId: cartello.id,
        proponenteSquadraId: miaSquadraId,
        importoMilioni: Number(importo),
      });
      setImporto("");
    } catch (e) {
      setErrore(errMsg(e));
    }
  }

  async function fRispondi(accetta: boolean) {
    if (!viva) return;
    setErrore(null);
    try {
      await rispondi.mutateAsync({ scommessaId: viva.id, accetta });
    } catch (e) {
      setErrore(errMsg(e));
    }
  }

  return (
    <div className="mt-3 border-t border-primary/10 pt-2">
      <div className="font-mono text-[10px] uppercase tracking-wider text-accent mb-1">
        {tr("Sfida P2P")}
      </div>

      {risolta && !viva && (
        <div className="text-xs text-fg-muted">
          Sfida risolta ({nomeSquadra(risolta.proponente_squadra_id)} vs{" "}
          {nomeSquadra(risolta.accettante_squadra_id)}) — {risolta.importo_milioni}M.
        </div>
      )}

      {viva && (
        <div className="text-xs text-fg space-y-1">
          <div>
            {nomeSquadra(viva.proponente_squadra_id)}{" "}
            <span className="text-fg-muted">{tr("sfida")}</span>{" "}
            {nomeSquadra(viva.accettante_squadra_id)} per{" "}
            <span className="font-bold">{viva.importo_milioni}M</span>.
          </div>
          {viva.stato === "proposta" &&
            viva.accettante_squadra_id === miaSquadraId &&
            bettabile && (
              <div className="flex gap-2">
                <button
                  onClick={() => fRispondi(true)}
                  disabled={rispondi.isPending}
                  className="text-xs font-bold bg-success/15 text-success border border-success/40 rounded-card px-3 py-1.5 hover:bg-success/25 transition disabled:opacity-50"
                >
                  {tr("Accetta")}
                </button>
                <button
                  onClick={() => fRispondi(false)}
                  disabled={rispondi.isPending}
                  className="text-xs font-bold bg-danger/10 text-danger border border-danger/30 rounded-card px-3 py-1.5 hover:bg-danger/20 transition disabled:opacity-50"
                >
                  {tr("Rifiuta")}
                </button>
              </div>
            )}
          {viva.stato === "proposta" && viva.proponente_squadra_id === miaSquadraId && (
            <div className="text-fg-muted">{tr("In attesa di risposta…")}</div>
          )}
          {viva.stato === "accettata" && (
            <div className="text-success">{tr("Sfida accettata · in gioco 🔥")}</div>
          )}
        </div>
      )}

      {!viva && bettabile && (
        <div className="flex items-end gap-2 mt-1">
          <label className="flex-1">
            <span className="font-mono text-[10px] text-fg-muted">{tr("Importo (mln)")}</span>
            <input
              type="number"
              step="0.5"
              min="0.5"
              value={importo}
              onChange={(e) => setImporto(e.target.value)}
              placeholder={tr("es. 5")}
              className="mt-1 w-full bg-bg-card border border-primary/30 rounded-card px-2 py-1.5 text-sm text-fg tabular-nums focus:border-accent/60 outline-none"
            />
          </label>
          <button
            onClick={fProponi}
            disabled={proponi.isPending || importo === ""}
            className="text-xs font-bold bg-accent/15 text-accent border border-accent/40 rounded-card px-3 py-2 hover:bg-accent/25 transition disabled:opacity-50"
          >
            {tr("Proponi sfida")}
          </button>
        </div>
      )}

      {!viva && !risolta && !bettabile && (
        <div className="text-xs text-fg-muted">{tr("Scommesse chiuse.")}</div>
      )}

      {errore && <div className="text-[11px] text-danger mt-2">⚠ {errore}</div>}
    </div>
  );
}

// ============================================================
// Blocco banco
// ============================================================
function BloccoBanco({
  cartello,
  mieEsterne,
  bancoMie,
  bettabile,
  nomeSquadra,
}: {
  cartello: CartelloItem;
  mieEsterne: string[];
  bancoMie: ScommessaBancoItem[];
  bettabile: boolean;
  nomeSquadra: (id: string) => string;
}) {
  const c = cartello;
  const piazza = usePiazzaBanco();
  const [squadraId, setSquadraId] = useState(mieEsterne[0] ?? "");
  const [pronostico, setPronostico] = useState<EsitoPartita>("1");
  const [importo, setImporto] = useState("");
  const [errore, setErrore] = useState<string | null>(null);

  const quota = (p: EsitoPartita): string | null =>
    p === "1" ? c.quota_1 : p === "x" ? c.quota_x : c.quota_2;

  async function fPiazza() {
    setErrore(null);
    try {
      await piazza.mutateAsync({
        cartelloId: c.id,
        squadraId: squadraId || mieEsterne[0],
        pronostico,
        importoMilioni: Number(importo),
      });
      setImporto("");
    } catch (e) {
      setErrore(errMsg(e));
    }
  }

  return (
    <div className="mt-3 border-t border-primary/10 pt-2">
      <div className="font-mono text-[10px] uppercase tracking-wider text-accent mb-1">
        {tr("Banco esterno")}
      </div>

      {/* Quote */}
      <div className="grid grid-cols-3 gap-2 mb-2 text-center">
        {(["1", "x", "2"] as EsitoPartita[]).map((p) => (
          <div key={p} className="bg-primary-deep/30 rounded-card py-1 border border-primary/20">
            <div className="font-mono text-[10px] text-fg-muted uppercase">{p}</div>
            <div className="font-display text-base text-fg tabular-nums">{quota(p) ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* Mie scommesse banco esistenti */}
      {bancoMie.length > 0 && (
        <div className="space-y-0.5 mb-2">
          {bancoMie.map((b) => (
            <div key={b.id} className="text-[11px] text-fg-muted">
              {nomeSquadra(b.squadra_id)}: {b.importo_milioni}M su {ESITO_LABEL[b.pronostico]} @{" "}
              {b.quota_applicata} ·{" "}
              <span
                className={
                  b.stato === "vinta"
                    ? "text-success"
                    : b.stato === "persa"
                      ? "text-danger"
                      : "text-fg-muted"
                }
              >
                {etichetta(b.stato)}
              </span>
            </div>
          ))}
        </div>
      )}

      {bettabile ? (
        <div className="space-y-2">
          {mieEsterne.length > 1 && (
            <select
              value={squadraId}
              onChange={(e) => setSquadraId(e.target.value)}
              className="w-full bg-bg-card border border-primary/30 rounded-card px-2 py-1.5 text-sm text-fg focus:border-accent/60 outline-none"
            >
              {mieEsterne.map((id) => (
                <option key={id} value={id}>
                  {nomeSquadra(id)}
                </option>
              ))}
            </select>
          )}
          <div className="flex gap-2">
            {(["1", "x", "2"] as EsitoPartita[]).map((p) => (
              <button
                key={p}
                onClick={() => setPronostico(p)}
                className={`flex-1 text-xs font-bold rounded-card py-1.5 border transition uppercase ${
                  pronostico === p
                    ? "bg-accent/20 text-accent border-accent/50"
                    : "bg-bg-card text-fg-muted border-primary/20 hover:bg-bg-card-hover"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <label className="flex-1">
              <span className="font-mono text-[10px] text-fg-muted">{tr("Importo (mln)")}</span>
              <input
                type="number"
                step="0.5"
                min="0.5"
                value={importo}
                onChange={(e) => setImporto(e.target.value)}
                placeholder={tr("es. 3")}
                className="mt-1 w-full bg-bg-card border border-primary/30 rounded-card px-2 py-1.5 text-sm text-fg tabular-nums focus:border-accent/60 outline-none"
              />
            </label>
            <button
              onClick={fPiazza}
              disabled={piazza.isPending || importo === "" || !quota(pronostico)}
              className="text-xs font-bold bg-accent/15 text-accent border border-accent/40 rounded-card px-3 py-2 hover:bg-accent/25 transition disabled:opacity-50"
            >
              {tr("Punta")}
            </button>
          </div>
          {importo !== "" && quota(pronostico) && (
            <div className="text-[11px] text-fg-muted">
              Vincita potenziale:{" "}
              <span className="text-success font-bold">
                {(Number(importo) * Number(quota(pronostico))).toFixed(2)}M
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-fg-muted">{tr("Scommesse chiuse.")}</div>
      )}

      {errore && <div className="text-[11px] text-danger mt-2">⚠ {errore}</div>}
    </div>
  );
}
